import { SkeletonChart } from './skeleton-chart';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler } from 'chart.js';
import { formatShortDate, formatParseFullDate } from '/src/utils/date';

ChartJS.defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';
ChartJS.defaults.font.size = 14;

ChartJS.register(Legend, LinearScale, CategoryScale, PointElement, LineElement, Filler);

export function Chart({ data = [], yScaleCallback = null, getMetricName = undefined, isLoading }) {
  const chartRef = useRef(null);
  const [gradient, setGradient] = useState(false);

  useEffect(() => {
    const chartInstance = chartRef.current;

    // wait for the chart to be drawn
    const updateGradient = () => {
      if (!chartInstance.chartArea) return;

      const { ctx, chartArea } = chartInstance;
      const { top, bottom } = chartArea;

      const gradient = ctx.createLinearGradient(0, top, 0, bottom);

      gradient.addColorStop(0, 'rgba(168, 92, 208, 0.2)');
      gradient.addColorStop(1, 'rgba(168, 92, 208, 0)');

      setGradient(gradient);
    };

    // call after first render
    if (chartRef.current && !gradient) {
      updateGradient();
    }

    // recalculate gradient on chart resize
    chartInstance.options.animation = {
      onComplete: () => {
        if (chartRef.current && !gradient) {
          updateGradient();
        }
      },
    };
  }, [gradient]);

  const labels = useMemo(() => {
    // return data?.days?.map?.((d) => formatShortDate(d.date)) ?? [];
    return data?.days?.map?.((d) => d.date) ?? [];
  }, [data]);

  const values = useMemo(() => {
    return data?.days?.map?.((d) => d.value) ?? [];
  }, [data]);

  const distributionData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: '#A85CD0',
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 5,
          pointStyle: 'circle',
          pointBackgroundColor: '#A85CD0',
        },
      ],
    };
  }, [values, labels, gradient]);

  const options = useMemo(() => {
    return {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      maintainAspectRatio: false,
      spanGaps: true,
      scales: {
        y: {
          suggestedMin: 0,
          suggestedMax: 1,
          ticks: {
            color: 'rgb(104, 118, 141)',
            stepSize: 1000,
            padding: 10,
            maxTicksLimit: 5,
            callback: yScaleCallback,
          },
          grid: {
            color: 'rgb(225, 230, 239)',
            lineWidth: 1,
          },
          border: {
            display: false,
          },
        },
        x: {
          ticks: {
            color: 'rgb(104, 118, 141)',
            padding: 15,
            maxTicksLimit: 7,
            callback: function (value, _index, _values) {
              const rawLabel = this.getLabelForValue(value);
              return formatShortDate(rawLabel);
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: (ctx) => externalTooltipHandler(ctx, yScaleCallback, getMetricName),
          callbacks: {
            title: (tooltipItems) => {
              const item = tooltipItems[0];
              return formatParseFullDate(item.label);
            },
          },
        },
        legend: {
          labels: false,
        },
        // mouseline: {
        //   enabled: true,
        // },
      },
    };
  }, [yScaleCallback, getMetricName]);

  if (isLoading) {
    return <SkeletonChart />;
  }

  return <Line ref={chartRef} data={distributionData} options={options} className="w-full h-auto" />;
}

const externalTooltipHandler = (context, formatter = null, metric = 'Visitors') => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    const bodyLines = tooltip.body.map((b) => b.lines);

    const tableHead = document.createElement('thead');

    if (tooltip.title && tooltip.title.length > 0) {
      const th = document.createElement('th');
      const titleText = document.createTextNode(tooltip.title[0]);
      th.appendChild(titleText);
      th.colSpan = 2;
      tableHead.appendChild(th);
      tableHead.style.textAlign = 'left';
      tableHead.style.color = '#0a1c2e';
      tableHead.style.fontWeight = '600';
      tableHead.style.fontSize = '13px';
    }

    const tableBody = document.createElement('tbody');

    bodyLines.forEach((body, i) => {
      const colors = tooltip.labelColors[i];

      // const span = document.createElement('span');
      // span.style.background = colors.borderColor;
      // span.style.borderColor = colors.borderColor;
      // span.style.borderRadius = '50%';
      // span.style.borderWidth = '2px';
      // span.style.marginRight = '15px';
      // span.style.height = '6px';
      // span.style.width = '6px';
      // span.style.display = 'inline-block';

      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = 0;

      const td1 = document.createElement('td');
      td1.style.borderWidth = 0;
      td1.style.verticalAlign = 'middle';
      td1.style.color = '#4B4B4B';
      td1.style.fontSize = '12px';
      td1.style.fontWeight = '400';

      const td2 = document.createElement('td');
      td2.style.borderWidth = 0;
      td2.style.paddingLeft = '20px';
      td2.style.fontWeight = '600';
      td2.style.color = colors.borderColor;

      // const [variantName, chartValue] = body[0].split(':').map((a) => a.trim());

      const vName = document.createTextNode(metric);
      const cValue = document.createTextNode(
        formatter ? formatter(body[0]?.toLocaleString?.()) : body[0]?.toLocaleString?.(),
      );

      td1.appendChild(vName);
      td2.appendChild(cValue);
      tr.appendChild(td1);
      tr.appendChild(td2);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY, offsetWidth: canvasWidth } = chart.canvas;

  // Calculate the initial tooltip position
  let left = positionX + tooltip.caretX;
  let top = positionY + tooltip.caretY - tooltipEl.offsetHeight - 2;

  // Adjust if the tooltip goes beyond the canvas on the right side
  if (left + tooltipEl.offsetWidth > positionX + canvasWidth) {
    left = positionX + canvasWidth - tooltipEl.offsetWidth - 10;
  }

  // Adjust if the tooltip goes beyond the canvas on the left side
  if (left < positionX) {
    left = positionX + 10; // 10px padding from the edge
  }

  // Adjust if the tooltip goes beyond the canvas on the top
  if (top < positionY) {
    top = positionY + tooltip.caretY + 10; // 10px padding below the cursor if too high
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top - 6}px`;
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.borderColor = '#E1E1E1';
  tooltipEl.style.borderWidth = '1px';
  tooltipEl.classList.add('shadow-lg', 'border', 'border-mystic-500', 'rounded', 'overflow-hidden', 'p-2.5');
};

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = '#fff';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.color = '#000';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transition = 'all .1s ease';

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};
